interface BuildV2RedirectUrlArgs {
  baseUrl: string;
  accessToken: string | null | undefined;
  /**
   * Must travel with the session. The access token lasts 24h; only this can
   * buy a new one, and v2 has no other way to obtain it — localStorage does
   * not cross origins. Dropping it here is what capped a handed-off session
   * at a single day and forced a daily re-login.
   */
  refreshToken: string | null | undefined;
  sessionToken: string | null | undefined;
  impersonateId: string | null | undefined;
  companyId: string | null | undefined;
  encKey: string | undefined;
}

function buildPlaintextUrl(args: BuildV2RedirectUrlArgs): string {
  const {
    baseUrl,
    accessToken,
    refreshToken,
    sessionToken,
    impersonateId,
    companyId,
  } = args;
  const params = new URLSearchParams();
  if (accessToken) params.set('accessToken', accessToken);
  if (refreshToken) params.set('refreshToken', refreshToken);
  if (sessionToken) params.set('sessionToken', sessionToken);
  if (impersonateId) params.set('impersonateId', impersonateId);
  if (companyId) params.set('companyId', companyId);
  return `${baseUrl}#${params.toString()}`;
}

export async function buildV2RedirectUrl(
  args: BuildV2RedirectUrlArgs,
): Promise<string> {
  const {
    baseUrl,
    accessToken,
    refreshToken,
    sessionToken,
    impersonateId,
    companyId,
    encKey,
  } = args;

  if (!encKey) return buildPlaintextUrl(args);

  try {
    const payload = {
      accessToken,
      refreshToken: refreshToken ?? null,
      sessionToken: sessionToken ?? null,
      impersonateId: impersonateId ?? null,
      companyId: companyId ?? null,
    };
    const raw = await crypto.subtle.digest(
      'SHA-256',
      new TextEncoder().encode(encKey),
    );
    const key = await crypto.subtle.importKey('raw', raw, 'AES-GCM', false, [
      'encrypt',
    ]);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(JSON.stringify(payload));
    const ciphertext = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      key,
      encoded,
    );
    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);
    const token = btoa(String.fromCharCode(...combined))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
    return `${baseUrl}#token=${token}`;
  } catch {
    return buildPlaintextUrl(args);
  }
}
